/**
 * ConversationMemoryControl — le rond de mémoire du composer, point d'entrée de
 * la mémoire de la conversation.
 *
 * Pourquoi un popover déguisé en infobulle : l'avocat doit pouvoir lire où en
 * est la mémoire ET la compacter au même endroit, mais l'encart titré qui portait
 * ces deux choses pesait plus lourd que l'information qu'il donnait. La forme est
 * donc celle d'une infobulle — une ligne d'état, l'action à sa suite — et la
 * mécanique reste celle d'un popover : une infobulle HTML ne se parcourt ni au
 * clavier ni au doigt, alors qu'elle contient ici un bouton.
 *
 * Pourquoi un pourcentage seul : un volume en « tokens » est un terme technique
 * anglais qui n'apprend rien à l'avocat ; la part de mémoire utilisée suffit.
 *
 * Pourquoi des libellés visibles plus courts que leurs noms accessibles : la
 * ligne n'a pas la place de « Compacter la conversation », mais un lecteur
 * d'écran qui annonce « Compacter » seul ne dit pas ce qui est compacté.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { ContextCompactionPhase } from '../../types/session.types';
import type { MemoryUsage } from '../../types/websocket.types';
import { formatMemoryPercent } from '../../utils/memoryUsageFormat';

/** Le seul champ de la jauge que le rond affiche. */
export type ConversationMemoryUsage = Pick<MemoryUsage, 'percentUsed'>;

export interface ConversationMemoryControlProps {
  /** Dernière mesure ; `null` tant qu'aucun échange ne l'a produite sur cette socket. */
  memoryUsage: ConversationMemoryUsage | null;
  /**
   * Offrir « Compacter la conversation ». L'hôte n'y met `true` que si la socket
   * a annoncé la capacité, que la conversation a des messages et que l'avocat
   * peut agir sur le fil.
   */
  canCompactContext: boolean;
  /** Phase de la compaction manuelle ; `running` fait tourner le rond. */
  compactionStatus: ContextCompactionPhase;
  /** Un tour est en cours ou en attente : compacter maintenant serait refusé. */
  isBusy: boolean;
  onCompactContext?: () => void;
}

const RING_RADIUS = 7.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Rond ouvrant la mémoire au survol, au clic ou au clavier. */
export function ConversationMemoryControl({
  memoryUsage,
  canCompactContext,
  compactionStatus,
  isBusy,
  onCompactContext,
}: ConversationMemoryControlProps): React.JSX.Element {
  const isCompacting = compactionStatus === 'running';
  const percentLabel = memoryUsage ? formatMemoryPercent(memoryUsage.percentUsed) : null;
  const filledRatio = memoryUsage ? Math.min(100, Math.max(0, memoryUsage.percentUsed)) / 100 : 0;

  const [open, setOpen] = React.useState(false);
  const openedByHover = React.useRef(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const cancelClose = () => clearTimeout(closeTimer.current);
  React.useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openOnHover = () => {
    cancelClose();
    if (!open) {
      openedByHover.current = true;
      setOpen(true);
    }
  };
  const closeAfterHover = () => {
    cancelClose();
    // Le contenu est portalisé : laisser traverser l'espace entre le rond et le
    // panneau, sans fermer un panneau ouvert au clavier ou contenant le focus.
    closeTimer.current = setTimeout(() => {
      if (openedByHover.current && !contentRef.current?.contains(document.activeElement)) {
        setOpen(false);
      }
    }, 180);
  };

  return (
    <Popover open={open} onOpenChange={(nextOpen) => {
      cancelClose();
      openedByHover.current = false;
      setOpen(nextOpen);
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={openOnHover}
          onMouseLeave={closeAfterHover}
          onClick={(event) => {
            // Un clic suivant le survol garde l'encart ouvert (tactile et
            // clavier conservent le déclenchement natif de Radix).
            if (openedByHover.current) {
              event.preventDefault();
              openedByHover.current = false;
              cancelClose();
            }
          }}
          aria-label="Mémoire de la conversation"
          aria-busy={isCompacting || undefined}
          className="flex h-8 w-8 items-center justify-center rounded-full text-grey-600 transition-colors hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {isCompacting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
              <circle cx="10" cy="10" r={RING_RADIUS} fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
              <circle
                cx="10"
                cy="10"
                r={RING_RADIUS}
                fill="none"
                stroke="#262626"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - filledRatio)}
                transform="rotate(-90 10 10)"
              />
            </svg>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        side="top"
        align="end"
        // Le dialog n'a plus de titre visible : il se nomme pour les lecteurs
        // d'écran, qui annonceraient sinon un panneau anonyme.
        aria-label="Mémoire de la conversation"
        className="w-auto max-w-[20rem] space-y-1 px-3 py-2"
        onMouseEnter={cancelClose}
        onMouseLeave={closeAfterHover}
        onOpenAutoFocus={(event) => {
          // Un survol ne doit pas voler le focus du brouillon en cours.
          if (openedByHover.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
      >
        <div className="flex items-center gap-1.5 whitespace-nowrap text-xs">
          <span className="text-charcoal">
            {memoryUsage ? `${percentLabel} de la mémoire utilisée` : 'Mesurée au prochain échange'}
          </span>

          {canCompactContext && (
            <>
              <span aria-hidden className="text-grey-300">
                ·
              </span>
              <button
                type="button"
                aria-label={isCompacting ? 'Compaction en cours…' : 'Compacter la conversation'}
                // Un bouton grisé sans raison laisse l'avocat sans recours : dire
                // ce qu'il attend plutôt que de le laisser cliquer dans le vide.
                title={isBusy && !isCompacting ? 'Attendez la fin de la réponse en cours' : undefined}
                disabled={isBusy || isCompacting}
                onClick={onCompactContext}
                className={cn(
                  'flex shrink-0 items-center gap-1 font-medium text-charcoal underline decoration-grey-300 underline-offset-2 transition-colors',
                  'hover:decoration-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:text-grey-400 disabled:no-underline',
                  isCompacting && 'cursor-wait',
                )}
              >
                {isCompacting ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    Compaction…
                  </>
                ) : (
                  'Compacter'
                )}
              </button>
            </>
          )}
        </div>

        {canCompactContext && (
          <p className="text-xs leading-relaxed text-grey-500">
            Hector résume les échanges anciens ; l&apos;historique reste affiché.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
